// Boîte à idées - Page personnelle pour noter ses idées avec dossiers personnalisés
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb, Plus, Share2, X, Trash2, Folder, FolderPlus,
  Clock, Loader2, Check, Edit2, Sparkles, ArrowLeft,
  Target, Megaphone, Settings, TrendingUp, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { API_URL } from '../../config/api';
import { toast } from 'sonner';

// Couleurs pour les dossiers
const FOLDER_COLORS = [
  '#E8650A', // Orange
  '#10B981', // Vert
  '#3B82F6', // Bleu
  '#EC4899', // Rose
  '#8B5CF6', // Violet
  '#F59E0B', // Ambre
  '#EF4444', // Rouge
  '#06B6D4', // Cyan
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

// ═══════════════════════════════════════════════════════════════════════════════
// DOSSIERS SUGGÉRÉS PRO (affichés quand pas de dossiers créés)
// ═══════════════════════════════════════════════════════════════════════════════
const SUGGESTED_FOLDERS_PRO = [
  { name: 'Produit', color: '#E8650A', icon: Lightbulb, description: 'Nouvelles offres, services' },
  { name: 'Marketing', color: '#EC4899', icon: Megaphone, description: 'Campagnes, promos' },
  { name: 'Process', color: '#3B82F6', icon: Settings, description: 'Amélioration méthodes' },
  { name: 'Commercial', color: '#10B981', icon: TrendingUp, description: 'Stratégies vente' },
  { name: 'Équipe', color: '#8B5CF6', icon: Users, description: 'RH, recrutement' },
];

export default function BoiteAIdeesPage({ onAskAbra, onBackToHub, isPro = false }) {
  const { user, token } = useCurrentUser();
  const [ideas, setIdeas] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewIdeaModal, setShowNewIdeaModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('all');
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showNewIdeaModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showNewIdeaModal, isMobile]);
  
  // Form state pour nouvelle idée
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    folder_id: null,
  });
  
  // Form state pour nouveau dossier
  const [newFolder, setNewFolder] = useState({
    name: '',
    color: FOLDER_COLORS[0],
  });
  
  const [submitting, setSubmitting] = useState(false);

  // Bloquer le scroll de la page quand un modal est ouvert
  useEffect(() => {
    if (showNewIdeaModal || showNewFolderModal) {
      // Bloquer le scroll du body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Restaurer le scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showNewIdeaModal, showNewFolderModal]);

  // Charger les dossiers et idées
  useEffect(() => {
    if (user && token) {
      fetchFolders();
      fetchIdeas();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  // Recharger les idées quand le dossier change
  useEffect(() => {
    if (user && token) {
      fetchIdeas();
    }
  }, [selectedFolder]);

  const fetchFolders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ideas/folders`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
    }
  };

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFolder !== 'all') params.append('folder_id', selectedFolder);
      
      const response = await fetch(`${API_URL}/api/ideas?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setIdeas(data.ideas || []);
      }
    } catch (error) {
      console.error('Erreur chargement idées:', error);
    } finally {
      setLoading(false);
    }
  };

  // Créer un nouveau dossier
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolder.name.trim()) {
      toast.error('Donne un nom au dossier');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/ideas/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newFolder),
      });

      if (response.ok) {
        toast.success('Dossier créé !');
        setNewFolder({ name: '', color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)] });
        setShowNewFolderModal(false);
        fetchFolders();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Soumettre une nouvelle idée
  const handleSubmitIdea = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Connecte-toi pour ajouter une idée');
      return;
    }
    if (!newIdea.title.trim()) {
      toast.error('Donne un titre à ton idée');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newIdea,
          folder_id: newIdea.folder_id || null,
        }),
      });

      if (response.ok) {
        toast.success('Idée ajoutée !');
        setNewIdea({ title: '', description: '', folder_id: null });
        setShowNewIdeaModal(false);
        fetchIdeas();
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer une idée
  const handleDelete = async (ideaId) => {
    if (!confirm('Supprimer cette idée ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/ideas/${ideaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Idée supprimée');
        setIdeas(ideas.filter(i => i.id !== ideaId));
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Supprimer un dossier
  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Supprimer ce dossier ? Les idées seront déplacées vers "Toutes".')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/ideas/folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Dossier supprimé');
        if (selectedFolder === folderId) setSelectedFolder('all');
        fetchFolders();
        fetchIdeas();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Partager une idée
  const handleShare = async (idea) => {
    const folderName = folders.find(f => f.id === idea.folder_id)?.name || '';
    const shareText = `💡 Mon idée${folderName ? ` (${folderName})` : ''} : ${idea.title}${idea.description ? `\n\n${idea.description}` : ''}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mon idée',
          text: shareText,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Idée copiée !');
    }
  };

  // Trouver le dossier d'une idée
  const getFolderInfo = (folderId) => {
    return folders.find(f => f.id === folderId) || null;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style harmonisé */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>
                Mes idées
              </h1>
              {!isMobile && (
                <p className="text-sm text-gray-500">
                  Organise tes idées par dossier
                </p>
              )}
            </div>
          </div>
          {isMobile ? (
            <button 
              onClick={() => setShowNewIdeaModal(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              Nouvelle idée
            </button>
          ) : (
            <Button
              onClick={() => setShowNewIdeaModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
            >
              <Plus size={16} />
              Nouvelle idée
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Lightbulb size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {ideas.length} idée{ideas.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Dossiers - Chips horizontaux style Liste de tâches */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => setSelectedFolder('all')}
          className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
            selectedFolder === 'all'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'
          }`}
        >
          <span className="flex items-center gap-1.5">
            <Folder size={14} />
            Toutes
          </span>
        </button>

        {/* Dossiers créés par l'utilisateur */}
        {folders.map((folder) => (
          <button
            key={folder.id}
            onClick={() => setSelectedFolder(folder.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              selectedFolder === folder.id
                ? 'text-white shadow-sm'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'
            }`}
            style={selectedFolder === folder.id ? { backgroundColor: folder.color } : {}}
          >
            <span className="flex items-center gap-1.5">
              <Folder size={14} />
              {folder.name}
            </span>
          </button>
        ))}
        
        {/* Dossiers suggérés PRO (quand pas de dossiers créés) */}
        {isPro && folders.length === 0 && SUGGESTED_FOLDERS_PRO.map((suggested) => {
          const SuggestedIcon = suggested.icon;
          return (
            <button
              key={suggested.name}
              onClick={async () => {
                // Créer le dossier suggéré
                try {
                  const response = await fetch(`${API_URL}/api/ideas/folders`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ name: suggested.name, color: suggested.color }),
                  });
                  if (response.ok) {
                    const data = await response.json();
                    setFolders([...folders, data.folder]);
                    setSelectedFolder(data.folder.id);
                    toast.success(`Dossier "${suggested.name}" créé`);
                  }
                } catch (error) {
                  toast.error('Erreur création dossier');
                }
              }}
              className="px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all bg-white text-gray-500 hover:bg-gray-50 border border-dashed border-gray-300"
              title={suggested.description}
            >
              <span className="flex items-center gap-1.5">
                <SuggestedIcon size={14} style={{ color: suggested.color }} />
                {suggested.name}
              </span>
            </button>
          );
        })}
        
        <button
          onClick={() => setShowNewFolderModal(true)}
          className="px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all bg-gray-50 text-gray-500 hover:bg-gray-100 border border-dashed border-gray-200"
        >
          <FolderPlus size={16} />
        </button>
      </div>

      {/* Liste des idées - Conteneur harmonisé */}
      <div className="flex-1 rounded-2xl bg-white border border-gray-100 overflow-y-auto">
        {!user ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-orange-50 flex items-center justify-center">
                <Lightbulb size={28} className="text-orange-500" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Connecte-toi</h3>
              <p className="text-sm text-gray-500">pour accéder à tes idées</p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : ideas.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
                <Lightbulb size={28} className="text-gray-300" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {selectedFolder === 'all' ? 'Aucune idée' : 'Dossier vide'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {selectedFolder === 'all' ? 'Note ta première idée !' : 'Ajoute une idée dans ce dossier'}
              </p>
              <Button
                onClick={() => {
                  setNewIdea({ ...newIdea, folder_id: selectedFolder === 'all' ? null : selectedFolder });
                  setShowNewIdeaModal(true);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
              >
                <Plus size={16} />
                Nouvelle idée
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 p-4 space-y-3">
            {ideas.map((idea) => (
              <IdeaCard 
                key={idea.id} 
                idea={idea} 
                folder={getFolderInfo(idea.folder_id)}
                onDelete={handleDelete}
                onShare={handleShare}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal nouvelle idée - Version mobile plein écran / Desktop centré */}
      {showNewIdeaModal && createPortal(
        <AnimatePresence>
          {isMobile ? (
            /* ═══ VERSION MOBILE - PLEIN ÉCRAN (Portal dans body) ═══ */
            <motion.div
              key="mobile-modal"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-[10002] flex flex-col bg-white"
            >
              {/* Header avec safe area */}
              <header 
                className="flex items-center gap-2 px-3 border-b border-gray-100"
                style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
              >
                <button 
                  onClick={() => setShowNewIdeaModal(false)}
                  className="p-1 text-gray-500"
                >
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Nouvelle idée</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                {/* Titre */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Ton idée *
                  </label>
                  <input
                    type="text"
                    value={newIdea.title}
                    onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                    placeholder="Ex: Réserver le restaurant pour l'anniversaire"
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    maxLength={150}
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Détails (optionnel)
                  </label>
                  <textarea
                    value={newIdea.description}
                    onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                    placeholder="Ajoute des détails..."
                    rows={4}
                    className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                    maxLength={500}
                  />
                </div>

                {/* Dossier - Style filtres premium horizontal */}
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">
                    Ranger dans
                  </label>
                  <div className="relative flex items-center">
                    <div 
                      className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      <button
                        type="button"
                        onClick={() => setNewIdea({ ...newIdea, folder_id: null })}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                          newIdea.folder_id === null
                            ? 'bg-orange-500 text-white shadow-sm'
                            : 'bg-white text-gray-600 border border-gray-200'
                        }`}
                      >
                        <Folder size={14} />
                        Sans dossier
                      </button>

                      {folders.map((folder) => {
                        const isSelected = newIdea.folder_id === folder.id;
                        return (
                          <button
                            key={folder.id}
                            type="button"
                            onClick={() => setNewIdea({ ...newIdea, folder_id: folder.id })}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                              isSelected
                                ? 'bg-orange-500 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200'
                            }`}
                          >
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: folder.color }} />
                            {folder.name}
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        onClick={() => {
                          setShowNewIdeaModal(false);
                          setShowNewFolderModal(true);
                        }}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 border-dashed border-gray-300 text-gray-500"
                      >
                        <FolderPlus size={14} />
                        Créer
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                {/* Bouton Créer avec Abracada */}
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ title: newIdea.title, description: newIdea.description, folder_id: newIdea.folder_id, isNew: true });
                      setShowNewIdeaModal(false);
                    } else {
                      toast.info('Demande à Abra de développer ton idée !');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                {/* Boutons Annuler et Créer */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowNewIdeaModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmitIdea}
                    disabled={submitting || !newIdea.title.trim()}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    {submitting ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            /* ═══ VERSION DESKTOP - Modal centré ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Nouvelle idée</h3>
                  <button onClick={() => setShowNewIdeaModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  {/* Titre */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Ton idée *</label>
                    <input
                      type="text"
                      value={newIdea.title}
                      onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                      placeholder="Ex: Réserver le restaurant pour l'anniversaire"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      maxLength={150}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Détails (optionnel)</label>
                    <textarea
                      value={newIdea.description}
                      onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                      placeholder="Ajoute des détails..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                      maxLength={500}
                    />
                  </div>

                  {/* Dossier */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Ranger dans</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setNewIdea({ ...newIdea, folder_id: null })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all border-2 ${
                          newIdea.folder_id === null 
                            ? 'bg-orange-50 border-orange-500 text-orange-600' 
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <Folder size={14} />
                        Sans dossier
                      </button>

                      {folders.map((folder) => {
                        const isSelected = newIdea.folder_id === folder.id;
                        return (
                          <button
                            key={folder.id}
                            type="button"
                            onClick={() => setNewIdea({ ...newIdea, folder_id: folder.id })}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all border-2 ${
                              isSelected 
                                ? 'border-orange-500' 
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                            style={isSelected ? { backgroundColor: `${folder.color}15`, borderColor: folder.color, color: folder.color } : {}}
                          >
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: folder.color }} />
                            {folder.name}
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        onClick={() => {
                          setShowNewIdeaModal(false);
                          setShowNewFolderModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400"
                      >
                        <FolderPlus size={14} />
                        Créer
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowNewIdeaModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ title: newIdea.title, description: newIdea.description, folder_id: newIdea.folder_id, isNew: true });
                          setShowNewIdeaModal(false);
                        } else {
                          toast.info('Demande à Abra de développer ton idée !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={handleSubmitIdea}
                      disabled={submitting || !newIdea.title.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {submitting ? 'Création...' : 'Créer l\'idée'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      , document.body)}

      {/* Modal nouveau dossier */}
      <AnimatePresence>
        {showNewFolderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-end sm:items-center justify-center"
            style={{ touchAction: 'none', zIndex: 10500 }}
            onClick={() => setShowNewFolderModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col"
              style={{ 
                maxHeight: '85vh',
                paddingBottom: 'env(safe-area-inset-bottom)',
                touchAction: 'pan-y',
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {/* Header modal - fixe */}
              <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-[#1a2a4a]">Nouveau dossier</h2>
                <button
                  onClick={() => setShowNewFolderModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Form - scrollable */}
              <form onSubmit={handleCreateFolder} className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du dossier
                  </label>
                  <input
                    type="text"
                    value={newFolder.name}
                    onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                    placeholder="Ex: Famille, Voyage, Travail..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#E8650A] focus:border-transparent outline-none"
                    maxLength={30}
                    autoFocus
                  />
                </div>

                {/* Couleur */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Couleur
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {FOLDER_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewFolder({ ...newFolder, color })}
                        className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                        style={{ 
                          backgroundColor: color,
                          border: newFolder.color === color ? '3px solid #1a2a4a' : '3px solid transparent',
                        }}
                      >
                        {newFolder.color === color && (
                          <Check size={18} className="text-white" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={submitting || !newFolder.name.trim()}
                  className="w-full bg-[#E8650A] hover:bg-[#d45a09] text-white py-3 rounded-xl h-12"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <FolderPlus size={18} className="mr-2" />
                  )}
                  Créer le dossier
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Composant carte idée
function IdeaCard({ idea, folder, onDelete, onShare }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
    >
      {/* Header avec dossier */}
      <div className="flex items-start justify-between mb-2">
        {folder ? (
          <div 
            className="flex items-center gap-1.5 px-2 py-1 rounded-full"
            style={{ backgroundColor: `${folder.color}15` }}
          >
            <div 
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: folder.color }}
            />
            <span 
              className="text-xs font-medium"
              style={{ color: folder.color }}
            >
              {folder.name}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100">
            <Folder size={12} className="text-gray-400" />
            <span className="text-xs font-medium text-gray-500">Sans dossier</span>
          </div>
        )}
        <span className="text-xs text-gray-400">
          {new Date(idea.created_at).toLocaleDateString('fr-FR')}
        </span>
      </div>

      {/* Content */}
      <h3 className="font-semibold text-[#1a2a4a] mb-1">{idea.title}</h3>
      {idea.description && (
        <p className="text-sm text-gray-600 mb-3">{idea.description}</p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={() => onShare(idea)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          <Share2 size={14} />
          <span className="text-sm font-medium">Partager</span>
        </button>
        <button
          onClick={() => onDelete(idea.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </motion.div>
  );
}
